import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import qs from 'query-string'
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core'
import moment from 'moment'

import {
  Container,
  ContentHeader,
  Page,
  LoadingFeedback,
  MenuButton,
  DialogLogs,
  TicketMessages,
  TicketInfo,
} from 'components'
import {
  MessageForm,
  ResponsibleForm,
  ComplaintTwoFactorDialog,
} from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useAuth from 'hooks/useAuth'
import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

import logoNotFound from 'images/not-found-ticket.svg'

const ComplaintShow = ({ match }) => {
  const [loading, setLoading] = useState(false)
  const [openLogDialog, setOpenLogDialog] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [responseLog, setResponseLog] = useState([])

  // 2FA states
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [twoFactorBlocked, setTwoFactorBlocked] = useState(false)
  const [twoFactorAccessToken, setTwoFactorAccessToken] = useState()
  const [twoFactorAuthToken, setTwoFactorAuthToken] = useState(() =>
    service.dponet.complaintTwoFactorStorage.getToken(),
  )

  const classes = useStyles()
  const history = useHistory()
  const scrollRef = useRef()
  const snackbar = useSnackbar()
  const { user, company } = useAuth()
  const permissions = usePermissions()

  const {
    BOX_MESSAGE_COLOR,
    BOX_MESSAGE_COLOR_BY_VISIBILITY,
    MESSAGE_TYPE_ICON_COLOR,
    MESSAGE_TYPE_ICON_COLOR_BY_VISIBILITY,
    VISIBILITY_LABEL,
  } = constants.complaint
  const { ptBrFormatDateTime } = helpers.formatters

  const { complaintId } = match?.params

  const shouldFetchComplaint = !twoFactorRequired && !twoFactorBlocked

  const { response, error, isLoading, refresh } = useFetch(
    service.dponet.complaint.get,
    {
      complaintId,
      twoFactorAuthToken,
    },
    [complaintId, twoFactorAuthToken, shouldFetchComplaint],
    shouldFetchComplaint,
  )

  const complaint =
    twoFactorRequired || twoFactorBlocked
      ? undefined
      : response?.data?.complaint

  const hasComplaintAccess = !!complaint
  const hasComplaintAccessRef = useRef(false)
  hasComplaintAccessRef.current = hasComplaintAccess

  useEffect(() => {
    const status = error?.response?.status

    if (status === 403) history.push(routes.forbidden)
    if (status === 404) history.push(routes.notFound)
  }, [error, history])

  useEffect(() => {
    if (!response) return

    if (response.status === 202) {
      if (!twoFactorRequired) {
        setTwoFactorAccessToken(response.data?.accessToken)
        setTwoFactorRequired(true)
      }
      return
    }

    if (response.status === 200) {
      setTwoFactorRequired(false)
      setTwoFactorBlocked(false)
    }
  }, [response, twoFactorRequired, twoFactorAuthToken])

  const { response: userCompaniesResponse, isLoading: userCompanyIsLoading } =
    useFetch(
      service.dponet.usersCompanies.list,
      { listByCompany: true, showSelf: true, toSelect: true, perPage: 99999 },
      [complaintId],
    )

  const userCompanies = userCompaniesResponse?.data?.userCompanies

  const reportClassification = complaint
    ? helpers.complaint.findAnswer(
        complaint,
        constants?.complaint?.REPORT_CLASSIFICATION_ANSWER_KIND,
      )
    : undefined

  const description = complaint
    ? helpers.complaint.findAnswer(
        complaint,
        constants.complaint?.DESCRIPTION_ANSWER_KIND,
      )
    : undefined

  const isComplaintResponsible = !!complaint?.currentUserResponsible

  const canManageIncident = permissions.some([
    constants.permissions.INCIDENTS.CREATE,
    constants.permissions.INCIDENTS.MANAGE,
  ])

  const handleRedirectIncident = () => {
    const query = qs.stringify({
      complaintId,
      convertedComplaintIncident: true,
    })

    history.push(routes.incidents.new + `?${query}`)
  }

  const complaintInfos = complaint
    ? helpers.complaint.mountComplaintInfos(
        complaint,
        classes,
        handleRedirectIncident,
        isComplaintResponsible,
        canManageIncident,
      )
    : []

  const backToListComplaints = () => history.push(routes.complaint.all)

  const handleOpenLogs = async () => {
    try {
      setLoadingLogs(true)
      const responseLogs = await service.dponet.complaint.logs({
        complaintId,
        twoFactorAuthToken,
      })
      const updatedlogs = helpers.logs.changeLogs(
        responseLogs?.data?.dponetAudits || [],
      )
      setResponseLog(updatedlogs)
      setOpenLogDialog(true)
    } catch (error) {
      setOpenLogDialog(false)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error, false) ||
          error?.error?.message,
        variant: 'error',
      })
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleTwoFactorSuccess = ({ authToken }) => {
    // Salva token e refaz a requisição automaticamente
    service.dponet.complaintTwoFactorStorage.setToken(authToken)
    setTwoFactorAuthToken(authToken)
    setTwoFactorAccessToken(undefined)
    setTwoFactorRequired(false)
    setTwoFactorBlocked(false)
  }

  const handleTwoFactorCancel = () => {
    // Fecha modal e bloqueia visualização até autenticação
    setTwoFactorRequired(false)
    setTwoFactorBlocked(true)
  }

  const getBoxColor = (complaintMessage) =>
    BOX_MESSAGE_COLOR_BY_VISIBILITY[complaintMessage?.visibility] ||
    BOX_MESSAGE_COLOR[complaintMessage?.kind]

  const getIconColor = (complaintMessage) =>
    MESSAGE_TYPE_ICON_COLOR_BY_VISIBILITY[complaintMessage?.visibility] ||
    MESSAGE_TYPE_ICON_COLOR[complaintMessage?.kind]

  useLayoutEffect(() => {
    if (scrollRef?.current) scrollRef.current.scrollIntoView()
  }, [])

  useEffect(() => {
    let elapsed = 0

    const timer = setInterval(() => {
      elapsed++
    }, 1000)

    return () => {
      clearInterval(timer)

      if (!hasComplaintAccessRef.current) return

      service.dponet.complaint.lastAccess({
        complaintId,
        twoFactorAuthToken,
        lastAcessInfo: {
          user: {
            id: user.id,
            name: user.name,
          },
          secondsSpentOnScreen: elapsed,
          date: moment(Date.now()).format('DD/MM/YYYY'),
        },
      })
    }
  }, [complaintId, user])

  return (
    <Page title="Visualizar Denúncia">
      <LoadingFeedback open={loading || isLoading || userCompanyIsLoading} />
      <Container>
        <ComplaintTwoFactorDialog
          accessToken={twoFactorAccessToken}
          onCancel={handleTwoFactorCancel}
          onSuccess={handleTwoFactorSuccess}
          open={twoFactorRequired}
        />
        <ContentHeader title="Visualizar Denúncia">
          <MenuButton>
            <Button color="secondary" fullWidth onClick={handleOpenLogs}>
              Histórico de alterações
            </Button>
          </MenuButton>
        </ContentHeader>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
          {!isLoading && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8} order={1}>
                <Box height="100%">
                  {description?.answerDecrypt ||
                  !isEmpty(complaint?.complaintMessages) ? (
                    <PerfectScrollbar
                      className={classes.containerMessages}
                      options={{
                        wheelPropagation: false,
                        useBothWheelAxes: true,
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        gridGap={theme.spacing(2)}
                        maxHeight={400}
                      >
                        <TicketMessages
                          title="Descrição do relato"
                          subTitle={`${reportClassification?.answerDecrypt} - ${reportClassification?.complaintAnswerSpecific?.answerDecrypt}`}
                          content={description?.answerDecrypt}
                          attachments={complaint?.attachments}
                          author={complaint?.authorName}
                          interactions={complaint?.complaintMessageInteractions}
                        />
                        {!isEmpty(complaint?.complaintMessages) && <Divider />}
                        {complaint?.complaintMessages?.map(
                          (complaintMessage, index) => (
                            <TicketMessages
                              key={index}
                              title={complaintMessage?.authorName}
                              subTitle={`${
                                VISIBILITY_LABEL[complaintMessage?.visibility]
                              } | ${company?.name} | ${ptBrFormatDateTime(
                                complaintMessage?.createdAt,
                              )}`}
                              content={complaintMessage?.content}
                              attachments={complaintMessage?.attachments}
                              author={complaintMessage?.authorName}
                              interactions={
                                complaintMessage?.complaintMessageInteractions
                              }
                              boxColor={getBoxColor(complaintMessage)}
                              iconColor={getIconColor(complaintMessage)}
                            />
                          ),
                        )}
                      </Box>
                      <span ref={scrollRef} />
                    </PerfectScrollbar>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      textAlign="center"
                      gridGap={theme.spacing(3)}
                      justifyContent="center"
                      height="100%"
                    >
                      <img
                        height={150}
                        src={logoNotFound}
                        alt="Nenhuma interação neste ticket"
                      />
                      <Typography
                        variant="h2"
                        className={classes.noInteractionTypography}
                      >
                        Nenhuma interação nesta denúncia
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              {!userCompanyIsLoading && !isLoading && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  component={Box}
                  display="flex"
                  flexDirection="column"
                  gridGap={theme.spacing(3)}
                  order={2}
                >
                  <ResponsibleForm
                    complaint={complaint}
                    complaintId={complaintId}
                    refresh={refresh}
                    setLoading={setLoading}
                    userCompanies={userCompanies}
                    twoFactorAuthToken={twoFactorAuthToken}
                  />
                  <TicketInfo items={complaintInfos} />
                </Grid>
              )}
            </Grid>
          )}
          {!isLoading && complaint && (
            <Box>
              <MessageForm
                complaintId={complaintId}
                complaint={complaint}
                setLoading={setLoading}
                refresh={refresh}
                twoFactorAuthToken={twoFactorAuthToken}
              />
            </Box>
          )}
          {!loadingLogs && (
            <DialogLogs
              logs={
                helpers.functions.isEmpty(responseLog) ? undefined : responseLog
              }
              open={openLogDialog}
              setOpen={setOpenLogDialog}
              showRegisterSession={false}
              refresh={handleOpenLogs}
            />
          )}
          <Box display="flex" justifyContent="end">
            <Button variant="contained" onClick={backToListComplaints}>
              Voltar
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  )
}

export default ComplaintShow
